/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Tue Nov 19 2024
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useRef } from "react";

import useWhiteboardStore from "../stores/useWhiteboardStore";
import Presets from "../presets";
import { MODE, PEN_TYPE, OBJECT_TYPE, DrawingController, DrawingManager } from "@drawing/index";

export type WhiteboardHook = ReturnType<typeof useWhiteboard> | undefined;

export default function useWhiteboard(userId: string) {
	const setUserId = useWhiteboardStore((state) => state.setUserId);
	const setPenColor = useWhiteboardStore((state) => state.setPenColor);
	const setToolState = useWhiteboardStore((state) => state.setToolState);
	const resetToolbarKey = useWhiteboardStore((state) => state.resetToolbarKey);
	const whiteboardMode = useWhiteboardStore((state) => state.whiteboardMode);

	const managerRef = useRef<DrawingManager>(new DrawingManager());
	const controllerRefs = useRef<{ [id: string]: DrawingController }>({});
	const canvasRef = useRef<HTMLCanvasElement>();
	const whiteboardModeRef = useRef(whiteboardMode);
	const animationFrameId = useRef<number | null>(null);

	/* User Id */
	useEffect(() => {
		setUserId(userId);
	}, [userId]);

	/* Initialize */
	const initialize = () => {
		// canvas
		canvasRef.current = managerRef.current.getOutputCtx()?.canvas as HTMLCanvasElement;
		// setup elements
		document.getElementById(Presets.containerId)?.appendChild(canvasRef.current);
		// add player
		addUser(userId);
		// reset
		resetWhiteboard();
		// start render
		startRender();
	};

	useEffect(() => {
		whiteboardModeRef.current = whiteboardMode;
	}, [whiteboardMode]);

	/* Start Render*/
	const startRender = () => {
		const render = () => {
			// render mangaer
			managerRef.current?.draw();
			// render palyers
			for (const id in controllerRefs.current) {
				controllerRefs.current[id].draw();
			}
			animationFrameId.current = requestAnimationFrame(render);
		};
		requestAnimationFrame(render);
	};

	/* Reset */
	const reset = () => {
		canvasRef.current?.remove();
		if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
	};

	const resetWhiteboard = () => {
		// reset history
		managerRef.current?.clearHistory();
		// reset mode
		updateMode(MODE.CURSOR);
		// reset defaults
		updateObjectStyle(Presets.defaultObjectStyle);
		// reset toolbars
		resetToolbarKey();
		// reset parent
		setParent("whiteboard-root");
		// reset resolution
		updateResolution({ width: window.innerWidth, height: window.innerHeight - 60 });
	};

	/* Update */
	const updateMode = (mode: MODE) => {
		controllerRefs.current[userId].changeMode(mode);
		updateCurrentToolState();
	};

	const updateObjectStyle = (styles: any) => {
		// save current color on store
		if (styles.strokeColor) setPenColor(styles.strokeColor);
		// set defaults
		if (styles.objectType && !styles.penType) styles.penType = PEN_TYPE.PEN;
		if (styles.penType && !styles.objectType) styles.objectType = OBJECT_TYPE.FREE_LINE;
		// update
		controllerRefs.current[userId].setObjectStyle(styles);
		updateCurrentToolState();
	};

	const updateResolution = (resolution: Resolution) => {
		// drawing일 경우 FHD로 고정
		const isDrawingMode = whiteboardModeRef.current === "drawing";
		const targetResolution = isDrawingMode ? { width: 1920, height: 1080 } : resolution;
		const targetCanvasResolution = {
			width: isDrawingMode ? "100%" : `${resolution.width}px`,
			height: isDrawingMode ? "100%" : `${resolution.height}px`
		};
		if (canvasRef.current) {
			canvasRef.current.style.width = targetCanvasResolution.width;
			canvasRef.current.style.height = targetCanvasResolution.height;
		}
		managerRef.current?.setResolution(targetResolution);
	};

	const updateImageButtonClicked = (filePath: string) => {
		updateMode(MODE.DRAWING);
		updateObjectStyle({ objectType: OBJECT_TYPE.IMAGE, imageSourcePath: filePath });
	};

	/* Users */
	const addUser = (id: string) => {
		controllerRefs.current[id] = new DrawingController(id, managerRef.current);
	};

	const removeUser = (id: string) => {
		delete controllerRefs.current[id];
	};

	/* Draw */
	const drawingStart = (
		id: string,
		position: Vector2,
		objectStyle?: IWhiteboardDrawingObject,
		force: boolean = false
	) => {
		const canvasPosition = getCanvasPosition(position);
		controllerRefs.current[id]?.drawingStart(canvasPosition, objectStyle, force);
	};

	const drawingTo = (id: string, positions: Vector2 | Vector2[], force: boolean = false) => {
		const canvasPositions = Array.isArray(positions)
			? positions.map(getCanvasPosition)
			: [getCanvasPosition(positions)];
		controllerRefs.current[id]?.drawingTo(canvasPositions, force);
	};

	const drawingEnd = (id: string, position: Vector2, force: boolean = false) => {
		const canvasPosition = getCanvasPosition(position);
		controllerRefs.current[id]?.drawingEnd(canvasPosition, force);

		// reset to pen mode if drawing image is done
		const currentObjectType = controllerRefs.current[userId].objectStyle.objectType;
		if (currentObjectType === "image") {
			updateObjectStyle({ objectType: OBJECT_TYPE.FREE_LINE });
		}
	};

	/* Presentation */
	const setParent = (targetDivId: string) => {
		const target = document.getElementById(targetDivId);
		const whiteboard = document.getElementById(Presets.containerId);
		if (target && whiteboard) target.appendChild(whiteboard);
	};

	/* Utils */
	const updateCurrentToolState = () => {
		const currentMode = controllerRefs.current[userId].mode ?? "cursor";
		const currentPenType = controllerRefs.current[userId].objectStyle.penType ?? "pen";
		const currentObjectType = controllerRefs.current[userId].objectStyle.objectType ?? "freeline";
		if (currentMode == MODE.DRAWING) {
			if (currentPenType == PEN_TYPE.PEN) setToolState(currentObjectType);
			else setToolState(currentPenType);
		} else {
			setToolState(currentMode);
		}
	};

	const getCanvasPosition = (position: Vector2) => {
		const canvas = canvasRef.current;
		if (!canvas) return position;
		const rect = canvas.getBoundingClientRect();
		const innerWidth = whiteboardModeRef.current === "drawing" ? rect.width : canvas.clientWidth;
		const innerHeight = whiteboardModeRef.current === "drawing" ? rect.height : canvas.clientHeight;
		const ratioX = canvas.width / innerWidth;
		const ratioY = canvas.height / innerHeight;
		return { x: position.x * ratioX, y: position.y * ratioY };
	};

	return {
		// initialize
		initialize,
		reset,
		resetWhiteboard,
		// user
		addUser,
		removeUser,
		// update
		updateMode: (mode: MODE) => updateMode(mode),
		updateObjectStyle: (styles: any) => updateObjectStyle(styles),
		updateResolution: (resolution: Resolution) => updateResolution(resolution),
		updateImageButtonClicked: (filePath: string) => updateImageButtonClicked(filePath),
		updateImageSrc: (id: string, filePath: string) => controllerRefs.current[id].setImageSrc(filePath),
		// drawing
		drawingStart,
		drawingTo,
		drawingEnd,
		// state
		updateHistoryState: {
			undo: () => managerRef.current?.undo(),
			redo: () => managerRef.current?.redo(),
			clearAll: () => managerRef.current?.clearAll()
		},
		// get info
		getCanvasRef: () => canvasRef.current,
		getCanDraw: () => controllerRefs.current[userId].mode == MODE.DRAWING,
		getObjectStyle: () => controllerRefs.current[userId].objectStyle,
		getCanvasResolution: () => ({
			width: canvasRef.current?.clientWidth ?? 1920,
			height: canvasRef.current?.clientHeight ?? 1080
		}),
		getCanvasRect: () => canvasRef.current?.getBoundingClientRect(),
		// presentation
		setParent: (targetDivId: string) => setParent(targetDivId),
		initPageCount: (pageCount: number) => managerRef.current.initPageCount(pageCount),
		updatePageNumber: (pageNumber: number) => managerRef.current.updatePageNumber(pageNumber)
	};
}
